import { create } from 'zustand';
import { messagesApi } from '../api';

const useMessageStore = create((set, get) => ({
  messages: [],
  conversations: {},
  currentConversation: null,
  quickReplies: [],
  unreadCount: 0,
  isLoading: false,
  error: null,

  setMessages: (messages) => set({ messages }),

  setCurrentConversation: (conversation) => set({ currentConversation: conversation }),

  fetchMessages: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await messagesApi.getAll(params);
      set({ messages: response.data.data || response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch messages', isLoading: false });
      throw error;
    }
  },

  fetchConversation: async (customerId, params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await messagesApi.getConversation(customerId, params);
      set((state) => ({
        conversations: { ...state.conversations, [customerId]: response.data },
        currentConversation: response.data,
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch conversation', isLoading: false });
      throw error;
    }
  },

  sendMessage: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await messagesApi.send(data);
      set((state) => {
        const customerId = data.customerId;
        const conversation = state.conversations[customerId] || [];
        return {
          conversations: {
            ...state.conversations,
            [customerId]: [...conversation, response.data]
          },
          isLoading: false
        };
      });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to send message', isLoading: false });
      throw error;
    }
  },

  sendMediaMessage: async (formData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await messagesApi.sendMedia(formData);
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to send media', isLoading: false });
      throw error;
    }
  },

  markAsRead: async (id) => {
    try {
      await messagesApi.markAsRead(id);
      set((state) => ({
        messages: state.messages.map(m => m.id === id ? { ...m, read: true } : m),
        unreadCount: Math.max(0, state.unreadCount - 1)
      }));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  },

  addMessage: (message) => {
    set((state) => {
      const customerId = message.customerId;
      const conversation = state.conversations[customerId] || [];
      return {
        messages: [message, ...state.messages],
        conversations: {
          ...state.conversations,
          [customerId]: [...conversation, message]
        },
        unreadCount: message.incoming ? state.unreadCount + 1 : state.unreadCount
      };
    });
  },

  fetchQuickReplies: async () => {
    try {
      const response = await messagesApi.getQuickReplies();
      set({ quickReplies: response.data });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch quick replies:', error);
      throw error;
    }
  },

  saveQuickReply: async (data) => {
    try {
      const response = await messagesApi.saveQuickReply(data);
      set((state) => ({ quickReplies: [...state.quickReplies, response.data] }));
      return response.data;
    } catch (error) {
      console.error('Failed to save quick reply:', error);
      throw error;
    }
  },

  deleteQuickReply: async (id) => {
    try {
      await messagesApi.deleteQuickReply(id);
      set((state) => ({ quickReplies: state.quickReplies.filter(qr => qr.id !== id) }));
    } catch (error) {
      console.error('Failed to delete quick reply:', error);
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set({
    messages: [],
    conversations: {},
    currentConversation: null,
    quickReplies: [],
    unreadCount: 0,
    isLoading: false,
    error: null
  }),
}));

export default useMessageStore;
